import { useState, useEffect, useMemo } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Typography } from "@mui/material";
import {
  FieldErrors,
  UseFormClearErrors,
  UseFormGetValues,
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
  useForm,
  useWatch,
} from "react-hook-form";
import { useLocation, useNavigate } from "react-router-dom";
import isEmpty from "lodash/isEmpty";
import type { InferType } from "yup";
import type { AnyObjectSchema } from "yup";

import { addSurgeonSchema, selectSurgeonSchema } from "../../yup";
import {
  useAddSurgeonMutation,
  useGetSurgeonsQuery,
  useUpdateSurgeonDefaultAddressMutation,
  useGetSurgeonDetailsQuery,
  useAddDoctorDeliveryAddressMutation,
  useAddDoctorToHealthOrgMutation,
} from "../../services/api";
import style from "./surgeon.module.css";
import { Button, LabelBox } from "../../components";
import { colors } from "../../theme/colors";
import { SingleSelectInput } from "../../components/SelectInput";
import { RootState, useAppSelector } from "../../store";
import AddressForm from "./components/AddressForm";
import AddressDetails from "./components/AddressDetails";
import { surgeonPrefix, DEFAULT_SUPER_ADMIN_ROLE_ID } from "../../utils/constants";
import { Input } from "../../components/UserInput";

type SurgeonFormState = typeof selectSurgeonSchema.__outputType;
type SelectOption = {
  id: number;
  label: string;
  value: number;
};

// `addSurgeonSchema` now lives in src/yup.ts alongside the other form schemas.
type AddSurgeonFormState = InferType<typeof addSurgeonSchema>;

/**
 * Surgeon creation/association page that supports selecting existing surgeons or creating new records
 * with address capture and validation.
 *
 * Returns:
 *   JSX.Element: Add Surgeon page layout with form controls.
 *
 * Notes:
 *   - Uses react-hook-form with Yup for validation.
 *   - Integrates RTK Query mutations; assumes surrounding auth provides health_org_id.
 */
const AddSurgeon = () => {
  const navigation = useNavigate();
  const location = useLocation();
  const selectedHealthOrgId =
    typeof location.state?.healthOrgId === "number"
      ? location.state.healthOrgId
      : undefined;
  const { userDetails } = useAppSelector(
    (state: RootState) => state.auth
  );
  const { specialities } = useAppSelector(
    (state: RootState) => state.referenceData
  );

  const isSuperAdmin =
    userDetails?.user_role_id === DEFAULT_SUPER_ADMIN_ROLE_ID;

  const [message, setMessage] = useState<LabelMessageType | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedSurgeonDetails, setSelectedSurgeonDetails] =
    useState<Surgeon>();
  const [isAddButton, setIsAddButton] = useState(isSuperAdmin);
  const [isAddNewAddress, setIsAddNewAddress] = useState(false);
  const [isEditAddress, setIsEditAddress] = useState(false);
  const [isSavingAddressDetails, setIsSavingAddressDetails] = useState(false);
  // Health org selection determines whether address is required in add flow.
  const resolvedHealthOrgId =
    userDetails?.health_org_id ?? selectedHealthOrgId ?? 0;
  const healthOrgPresent =
    resolvedHealthOrgId != null && resolvedHealthOrgId !== 0;
  const shouldRequireAddressInAddFlow =
    isAddButton && (!isSuperAdmin || healthOrgPresent);

  const { data: surgeonDetails, refetch: getSurgeonDetails } = useGetSurgeonDetailsQuery(
    { id: (selectedSurgeonDetails?.id ?? "").toString(), healthOrgId: resolvedHealthOrgId },
    { skip: !selectedSurgeonDetails?.id }
  );

  const [addSurgeon] = useAddSurgeonMutation();
  const [addDoctorToHealthOrg] = useAddDoctorToHealthOrgMutation();

  const [addDoctorDeliveryAddress] = useAddDoctorDeliveryAddressMutation();

  const [updateSurgeonDefaultAddress] =
    useUpdateSurgeonDefaultAddressMutation();

  // Ensure manual-add flow is always enabled for super admins, even if
  // userDetails loads after initial render.
  useEffect(() => {
    if (isSuperAdmin && !isAddButton) {
      setIsAddButton(true);
    }
  }, [isSuperAdmin, isAddButton]);

  // Keep selected surgeon details in sync with latest fetched server record.
  useEffect(() => {
    if (surgeonDetails) {
      setSelectedSurgeonDetails(surgeonDetails);
    }
  }, [surgeonDetails]);

  const { data: surgeonData } = useGetSurgeonsQuery({
    is_include_all_doctor: true,
    order_by_columns: "surname",
    order: "Asc",
    doctor_id: undefined,
    is_active: true,
  });

  /**
   * Builds select options for surgeon specialities.
   *
   * Returns:
   *   SelectOption[]: Speciality options with label/value pairs.
   */
  const surgeonSpecialities = useMemo<SelectOption[]>(
    () =>
      specialities
        ?.filter((speciality) => typeof speciality?.id === "number")
        .map((speciality) => ({
          id: speciality?.id ?? 0,
          label: speciality?.name ?? "",
          value: speciality?.id ?? 0,
        })) ?? [],
    [specialities]
  );

  /**
   * Builds select options for existing surgeons.
   *
   * Returns:
   *   SelectOption[]: Surgeon options formatted as "SURNAME, Given Prefix".
   *
   * Notes:
   *   - Uses local formatting to match existing select display.
   */
  const allSurgeons = useMemo<SelectOption[]>(
    () =>
      surgeonData?.items
        ?.filter((surgeon: Surgeon) => typeof surgeon?.id === "number")
        .map((surgeon: Surgeon) => {
          const surnameRaw = surgeon?.surname ?? "";
          const givenNameRaw = surgeon?.given_name ?? "";
          const prefixRaw = surgeon?.prefix ?? "";
          const surname =
            typeof surnameRaw === "string" ? surnameRaw : String(surnameRaw ?? "");
          const givenName =
            typeof givenNameRaw === "string" ? givenNameRaw : String(givenNameRaw ?? "");
          const prefix =
            typeof prefixRaw === "string" ? prefixRaw : String(prefixRaw ?? "");
          const formatted = `${surname ? surname.toUpperCase() : ""}, ${givenName} ${prefix}`.trim();
          return {
            id: surgeon?.id ?? 0,
            label: formatted,
            value: surgeon?.id ?? 0,
          };
        }) ?? [],
    [surgeonData]
  );

  /*
   * useForm hook from react-hook-form
   * Manages form state, validation, and submission logic.
   * - register: Registers input fields and gathers their values.
   * - handleSubmit: Triggers the provided callback on form submission.
   * - formState: Provides access to form state, such as errors
   * - yupResolver(surgeonSchema): Restrict the type of payload data for required, min length etc
   */
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch: detailsWatch,
    clearErrors,
    getValues,
  } = useForm<AddSurgeonFormState>({
    resolver: yupResolver(addSurgeonSchema),
    mode: "onChange",
    context: {
      selectedSurgeonDetails,
      isAddButton,
      shouldRequireAddressInAddFlow,
    },
  });

  // When a surgeon is selected from the list, hydrate the form with server data.
  useEffect(() => {
    // When surgeon details are fetched, sync them into form state to keep display in lockstep with server data.
    if (surgeonDetails) {
      const speciality = specialities.find(
        (item) => item.id === (surgeonDetails?.speciality_id ?? 0)
      );
      setSelectedSurgeonDetails(surgeonDetails);
      setValue("prefix", surgeonDetails?.prefix ?? "");
      setValue("speciality_id", speciality?.id);
      setValue("given_name", surgeonDetails?.given_name ?? "");
      setValue("surname", surgeonDetails?.surname ?? "");
    }
  }, [
    setValue,
    specialities,
    surgeonDetails?.given_name,
    surgeonDetails?.id,
    surgeonDetails?.prefix,
    surgeonDetails?.speciality_id,
    surgeonDetails?.surname,
  ]);

  const {
    register: surgeonRegister,
    control: surgeonControl,
    formState: { errors: surgeonErrors },
  } = useForm<SurgeonFormState>({
    resolver: yupResolver(selectSurgeonSchema),
    mode: "onChange",
  });

  const surgeonSelection = useWatch({
    control: surgeonControl,
    name: "surgeon",
  });

  // Selection drives association vs creation flow; keep state aligned with choice.
  useEffect(() => {
    if (surgeonSelection) {
      const filteredSurgeonDetails = surgeonData?.items?.find(
        (surgeon: Surgeon) => surgeon?.id === surgeonSelection
      );
      if (filteredSurgeonDetails) {
        setSelectedSurgeonDetails(filteredSurgeonDetails);
      }
    } else {
      setSelectedSurgeonDetails(undefined);
    }
  }, [allSurgeons, surgeonData?.items, surgeonSelection]);

  /**
   * Submits surgeon creation or association based on selection state.
   *
   * Args:
   *   values (AddSurgeonFormState): Form values from react-hook-form.
   *
   * Returns:
   *   Promise<void>
   *
   * Notes:
   *   - When a surgeon is selected, creates association with health org.
   *   - Otherwise, creates a new surgeon record.
   */
  // Submit branches: associate existing surgeon or create a new record.
  const onSubmit = async (values: AddSurgeonFormState) => {
    // Branch chooses association vs creation; both paths must preserve navigation and messaging semantics.
    setIsLoading(true);
    try {
      if (selectedSurgeonDetails) {
        if (!resolvedHealthOrgId || !selectedSurgeonDetails.id) {
          setMessage({
            type: "error",
            text: "Please enter valid details",
          });
          setIsLoading(false);
          return;
        }
        await addDoctorToHealthOrg({
          doctor_id: Number(selectedSurgeonDetails?.id ?? "0"),
          health_org_id: resolvedHealthOrgId,
        }).unwrap();
        setMessage({
          type: "success",
          text: "Doctor added successfully.",
        });
        setTimeout(() => {
          navigation(-1);
        }, 1500);
      } else {
        const selectedSpeciality = surgeonSpecialities?.find(
          (speciality) => speciality.value === values.speciality_id
        );
        const hasScopedHealthOrg = isSuperAdmin && healthOrgPresent;
        const healthOrgIdForPayload = isSuperAdmin
          ? hasScopedHealthOrg
            ? resolvedHealthOrgId
            : undefined
          : resolvedHealthOrgId || null;
        // Address fields are optional unless a health org context requires them.
        const addressFields = {
          phone: values.phone ?? null,
          provider_number: values.provider_number ?? null,
          fax: values.fax ?? null,
          mail: values.mail ?? null,
          unit_number: values.unit_number ?? null,
          street_name: values.street_name ?? null,
          street_number: values.street_number ?? null,
          suburb: values.suburb ?? "",
          state: values.state ?? null,
          postcode: values.postcode ?? null,
        };
        // Only include address payload if at least one address field is provided.
        const hasAnyAddressField = Object.values(addressFields).some(
          (v) => v != null && String(v).trim() !== ""
        );
        const doctorPayload = {
          given_name: values.given_name ?? "",
          surname: values.surname ?? "",
          prefix: values.prefix ?? "",
          speciality_id: selectedSpeciality?.id,
          health_org_id: healthOrgIdForPayload,
          is_associated: isSuperAdmin ? hasScopedHealthOrg : true,
          ...(hasAnyAddressField && { address: addressFields }),
        };

        await addSurgeon(doctorPayload).unwrap();
        setMessage({
          type: "success",
          text: "Doctor added successfully.",
        });
        setTimeout(() => {
          navigation(-1);
        }, 1500);
      }
    } catch (error: any) {
      setMessage({
        type: "error",
        text: error?.data?.detail?.toString() || "Please enter valid details",
      });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Updates the default address for the selected surgeon.
   *
   * Args:
   *   addressPayload (UpdateDeliveryAddressPayload): Payload including ids.
   *
   * Returns:
   *   Promise<void>
   */
  // Default address updates must include all ids for backend linkage.
  const updateDefaultAddress = async (
    addressPayload: UpdateDeliveryAddressPayload
  ) => {
    if (
      !addressPayload.doctor_id ||
      !addressPayload.doctor_delivery_address_id ||
      !addressPayload.health_org_id
    ) {
      setMessage({
        type: "error",
        text: "Something went wrong. Please try again later.",
      });
      return;
    }
    setIsSavingAddressDetails(true);
    try {
      await updateSurgeonDefaultAddress(addressPayload).unwrap();
      await getSurgeonDetails();
    } catch (error: any) {
      setMessage({
        type: "error",
        text:
          error?.message?.toString() ||
          "Something went wrong. Please try again later.",
      });
    } finally {
      setIsSavingAddressDetails(false);
      setIsAddNewAddress(false);
      setIsEditAddress(false);
    }
  };

  /**
   * Creates a new delivery address for the selected surgeon.
   *
   * Args:
   *   addressPayload (DeliveryAddressPayload): Address details.
   *
   * Returns:
   *   Promise<void>
   */
  // New addresses must be linked to both surgeon and health org.
  const addNewAddress = async (addressPayload: DeliveryAddressPayload) => {
    if (!surgeonDetails?.id || !resolvedHealthOrgId) {
      setMessage({
        type: "error",
        text: "Please enter valid details",
      });
      return;
    }
    setIsSavingAddressDetails(true);
    try {
      const doctor_id = Number(surgeonDetails?.id ?? 0);
      const health_org_id = resolvedHealthOrgId;
      // Address additions must pass health_org_id/is_associated to align with backend linkage expectations.
      await addDoctorDeliveryAddress({
        doctor_id,
        health_org_id: health_org_id,
        is_associated: true,
        address: addressPayload,
      }).unwrap();
      setIsAddNewAddress(false);
      await getSurgeonDetails();
    } catch (error: any) {
      setMessage({
        type: "error",
        text: error?.message?.toString() || "Please enter valid details",
      });
    } finally {
      setIsSavingAddressDetails(false);
    }
  };

  const shouldShowSelector =
    !isSuperAdmin && isEmpty(selectedSurgeonDetails) && !isAddButton;

  /**
   * Handles address form save in either embedded or standalone mode.
   *
   * Args:
   *   deliveryAddress (DeliveryAddressPayload): Address form payload.
   *
   * Returns:
   *   void
   *
   * Notes:
   *   - In manual add flow, updates local form values.
   *   - Otherwise, persists via API mutation.
   */
  // Address form is embedded for manual add, or persisted via API when associating.
  const handleAddressSave = (deliveryAddress: DeliveryAddressPayload) => {
    const payload: DeliveryAddressPayload = {
      ...deliveryAddress,
      doctor_id: selectedSurgeonDetails?.id ?? 0,
    };
    if (isAddButton) {
      setValue("unit_number", payload?.unit_number);
      setValue("street_number", payload?.street_number);
      setValue("street_name", payload?.street_name);
      setValue("state", payload?.state);
      setValue("suburb", payload.suburb);
      setValue("postcode", payload.postcode);
      setValue("latitude", payload.latitude);
      setValue("longitude", payload.longitude);
      setValue("mail", payload.mail);
      setValue("phone", payload.phone);
      setValue("provider_number", payload.provider_number);
      setValue("fax", payload.fax);
    } else {
      addNewAddress(payload);
    }
  };

  /**
   * Persists a new default address selection for a surgeon.
   *
   * Args:
   *   address (AddressDetails | undefined): Address chosen as default.
   *
   * Returns:
   *   void
   */
  // Default selection flows through API to avoid stale local state.
  const handleDefaultAddressUpdate = (address?: AddressDetails) => {
    if (!address) return;
    const payload: UpdateDeliveryAddressPayload = {
      doctor_delivery_address_id: Number(address.id ?? "0"),
      doctor_id: selectedSurgeonDetails?.id ?? 0,
      health_org_id: resolvedHealthOrgId,
      is_associated: true,
    };
    updateDefaultAddress(payload);
    setIsEditAddress(false);
    setIsAddNewAddress(false);
  };

  return (
    <Box className={style.container}>
      <Typography className={style.heading}>Add Surgeon</Typography>
      <Typography className={style.description}>
        Enter the below details to start adding a new surgeon to Pegasus
        Cybersoft.
      </Typography>
      <Box>
        <Box className={style.flexTop}>
          {shouldShowSelector ? (
            <SelectExistingSurgeon
              surgeonRegister={surgeonRegister}
              errors={surgeonErrors}
              surgeons={allSurgeons ?? []}
              onAddManually={() => setIsAddButton(true)}
            />
          ) : (
            <SurgeonFormSection
              register={register}
              errors={errors}
              detailsWatch={detailsWatch}
              surgeonSpecialities={surgeonSpecialities ?? []}
              selectedSurgeonDetails={selectedSurgeonDetails}
              onAddNewAddressClick={() => {
                setIsAddNewAddress(true);
              }}
              isAddNewAddress={isAddNewAddress}
              isAddButton={isAddButton}
              onAddressSave={handleAddressSave}
              onCancelAddressForm={() => setIsAddNewAddress(false)}
              isSavingAddressDetails={isSavingAddressDetails}
              addSurgeonSchema={addSurgeonSchema}
              setValue={setValue}
              clearErrors={clearErrors}
              getValues={getValues}
              onUpdateDefaultAddress={handleDefaultAddressUpdate}
            addressFieldsOptional={isSuperAdmin}
            />
          )}
        </Box>
        {message && (
          <Box mt={2}>
            <LabelBox
              boxProps={{ height: 50, display: "flex", alignItems: "center" }}
              data={message}
              onClose={() => setMessage(null)}
            />
          </Box>
        )}
        {(isAddButton ||
          (!isEmpty(selectedSurgeonDetails) &&
            !isAddNewAddress &&
            !isEditAddress)) && (
          <Box className={style.buttons} sx={{ marginBottom: "15px" }}>
            <Button
              sx={{
                width: "200px",
                height: "55px",
                ":hover": { backgroundColor: "transparent" },
              }}
              variant="outlined"
              onClick={() => navigation(-1)}
              label={"Cancel"}
            />
            <Button
              sx={{
                width: "200px",
                height: "55px",
                ":hover": { backgroundColor: colors.primary },
              }}
              variant="contained"
              label={"Save"}
              loading={isLoading}
              onClick={handleSubmit(onSubmit)}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

interface SelectExistingSurgeonProps {
  surgeonRegister: UseFormRegister<SurgeonFormState>;
  errors: FieldErrors<SurgeonFormState>;
  surgeons: SelectOption[];
  onAddManually: () => void;
}

/**
 * Provides an existing surgeon selector with a manual add option.
 *
 * Args:
 *   surgeonRegister (any): react-hook-form register for the select field.
 *   errors (any): Form errors bag.
 *   surgeons (any[]): Options for the surgeon select.
 *   onAddManually (() => void): Handler to switch to manual add flow.
 *
 * Returns:
 *   JSX.Element: Surgeon selector layout.
 */
/**
 * Provides an existing surgeon selector with a manual add option.
 *
 * Args:
 *   surgeonRegister (UseFormRegister<SurgeonFormState>): Register for selector.
 *   errors (FieldErrors<SurgeonFormState>): Form errors bag.
 *   surgeons (SelectOption[]): Options for the surgeon select.
 *   onAddManually (() => void): Handler to switch to manual add flow.
 *
 * Returns:
 *   JSX.Element: Surgeon selector layout.
 */

const SelectExistingSurgeon = ({
  surgeonRegister,
  errors,
  surgeons,
  onAddManually,
}: SelectExistingSurgeonProps) => (
  <Box>
    <Box className={style.row}>
      <SingleSelectInput
        placeholder="Select Surgeon"
        label="Select Surgeon"
        required
        register={surgeonRegister}
        name="surgeon"
        errors={errors}
        data={surgeons}
      />
    </Box>
    <Box sx={{ marginTop: 3, marginBottom: 3 }}>
      <Typography
        sx={{
          textAlign: "center",
          fontSize: "18px",
          fontWeight: "600",
        }}
      >
        Or
      </Typography>
      <Box
        sx={{ cursor: "pointer" }}
        onClick={onAddManually}
      >
        <Typography
          sx={{
            textAlign: "center",
            fontSize: "18px",
            textDecoration: "underline",
            fontWeight: "600",
            marginTop: 2,
          }}
        >
          Can't find the surgeon? Add Manually
        </Typography>
      </Box>
    </Box>
  </Box>
);

interface SurgeonFormSectionProps {
  register: UseFormRegister<AddSurgeonFormState>;
  errors: FieldErrors<AddSurgeonFormState>;
  detailsWatch: UseFormWatch<AddSurgeonFormState>;
  surgeonSpecialities: SelectOption[];
  selectedSurgeonDetails?: Surgeon;
  onAddNewAddressClick: () => void;
  isAddNewAddress: boolean;
  isAddButton: boolean;
  onAddressSave: (deliveryAddress: DeliveryAddressPayload) => void;
  onCancelAddressForm: () => void;
  isSavingAddressDetails: boolean;
  addSurgeonSchema: AnyObjectSchema;
  setValue: UseFormSetValue<AddSurgeonFormState>;
  clearErrors: UseFormClearErrors<AddSurgeonFormState>;
  getValues: UseFormGetValues<AddSurgeonFormState>;
  onUpdateDefaultAddress: (address?: AddressDetails) => void;
  addressFieldsOptional?: boolean;
}

/**
 * Composes surgeon fields and address manager for the add/associate flow.
 *
 * Args:
 *   register (any): react-hook-form register for surgeon fields.
 *   errors (any): Form error bag.
 *   detailsWatch (any): Watcher for current surgeon form values.
 *   surgeonSpecialities (any[]): Speciality options.
 *   selectedSurgeonDetails (Surgeon | undefined): Currently selected surgeon when associating.
 *   onAddNewAddressClick (() => void): Handler to show new address form.
 *   isAddNewAddress (boolean): Flag indicating new address form visibility.
 *   isAddButton (boolean): Indicates manual-add surgeon flow.
 *   onAddressSave ((DeliveryAddressPayload) => void): Callback for address save.
 *   onCancelAddressForm (() => void): Cancel handler for address form.
 *   isSavingAddressDetails (boolean): Loading flag for address save operations.
 *   addSurgeonSchema (any): Validation schema used when embedding the address form.
 *   setValue (any): Setter for form values (react-hook-form).
 *   clearErrors (any): Error clearer for form fields.
 *   getValues (any): Value getter for form fields.
 *   onUpdateDefaultAddress ((address?: AddressDetails) => void): Handler to mark address as default.
 *
 * Returns:
 *   JSX.Element: Combined surgeon form and address manager.
 */
/**
 * Composes surgeon fields and address manager for the add/associate flow.
 *
 * Args:
 *   register (UseFormRegister<AddSurgeonFormState>): Form register.
 *   errors (FieldErrors<AddSurgeonFormState>): Form error bag.
 *   detailsWatch (UseFormWatch<AddSurgeonFormState>): Watcher for form values.
 *   surgeonSpecialities (SelectOption[]): Speciality options.
 *   selectedSurgeonDetails (Surgeon | undefined): Selected surgeon context.
 *   onAddNewAddressClick (() => void): Handler to open address form.
 *   isAddNewAddress (boolean): New address form visibility.
 *   isAddButton (boolean): Manual add flow toggle.
 *   onAddressSave ((DeliveryAddressPayload) => void): Address save callback.
 *   onCancelAddressForm (() => void): Cancel handler for address form.
 *   isSavingAddressDetails (boolean): Address mutation loading flag.
 *   addSurgeonSchema (AnyObjectSchema): Validation schema for embedded flow.
 *   setValue (UseFormSetValue<AddSurgeonFormState>): Form value setter.
 *   clearErrors (UseFormClearErrors<AddSurgeonFormState>): Clears field errors.
 *   getValues (UseFormGetValues<AddSurgeonFormState>): Reads current values.
 *   onUpdateDefaultAddress ((address?: AddressDetails) => void): Default setter.
 *   addressFieldsOptional (boolean | undefined): Address optionality toggle.
 *
 * Returns:
 *   JSX.Element: Combined surgeon form and address manager.
 */
const SurgeonFormSection = ({
  register,
  errors,
  detailsWatch,
  surgeonSpecialities,
  selectedSurgeonDetails,
  onAddNewAddressClick,
  isAddNewAddress,
  isAddButton,
  onAddressSave,
  onCancelAddressForm,
  isSavingAddressDetails,
  addSurgeonSchema,
  setValue,
  clearErrors,
  getValues,
  onUpdateDefaultAddress,
  addressFieldsOptional,
}: SurgeonFormSectionProps) => (
  <Box>
    <Box style={{ marginTop: 10 }}>
      <SurgeonFormFields
        register={register}
        errors={errors}
        detailsWatch={detailsWatch}
        surgeonSpecialities={surgeonSpecialities}
        selectedSurgeonDetails={selectedSurgeonDetails}
      />
      <SurgeonAddressManager
        selectedSurgeonDetails={selectedSurgeonDetails}
        onAddNewAddressClick={onAddNewAddressClick}
        isAddNewAddress={isAddNewAddress}
        isAddButton={isAddButton}
        onAddressSave={onAddressSave}
        onCancelAddressForm={onCancelAddressForm}
        isSavingAddressDetails={isSavingAddressDetails}
        addSurgeonSchema={addSurgeonSchema}
        register={register}
        errors={errors}
        setValue={setValue}
        clearErrors={clearErrors}
        getValues={getValues}
        onUpdateDefaultAddress={onUpdateDefaultAddress}
        addressFieldsOptional={addressFieldsOptional}
      />
    </Box>
  </Box>
);

interface SurgeonFormFieldsProps {
  register: UseFormRegister<AddSurgeonFormState>;
  errors: FieldErrors<AddSurgeonFormState>;
  detailsWatch: UseFormWatch<AddSurgeonFormState>;
  surgeonSpecialities: SelectOption[];
  selectedSurgeonDetails?: Surgeon;
}

/**
 * Renders core surgeon identity fields with validation bindings.
 *
 * Args:
 *   register (any): react-hook-form register.
 *   errors (any): Form errors bag.
 *   detailsWatch (any): Watcher for form values to set controlled defaults.
 *   surgeonSpecialities (any[]): Options for speciality selection.
 *   selectedSurgeonDetails (Surgeon | undefined): When provided, fields are read-only.
 *
 * Returns:
 *   JSX.Element: Form fields block for surgeon details.
 */
/**
 * Renders core surgeon identity fields with validation bindings.
 *
 * Args:
 *   register (UseFormRegister<AddSurgeonFormState>): Form register.
 *   errors (FieldErrors<AddSurgeonFormState>): Form errors bag.
 *   detailsWatch (UseFormWatch<AddSurgeonFormState>): Watcher for defaults.
 *   surgeonSpecialities (SelectOption[]): Speciality options.
 *   selectedSurgeonDetails (Surgeon | undefined): Selected surgeon context.
 *
 * Returns:
 *   JSX.Element: Form fields block for surgeon details.
 */
const SurgeonFormFields = ({
  register,
  errors,
  detailsWatch,
  surgeonSpecialities,
  selectedSurgeonDetails,
}: SurgeonFormFieldsProps) => (
  <Box className={style.flexTop}>
    <Box className={style.row}>
      <SingleSelectInput
        placeholder="Select Prefix"
        label="Select Prefix"
        required
        register={register}
        name="prefix"
        errors={errors}
        data={surgeonPrefix ?? []}
        value={detailsWatch("prefix") ?? ""}
        disabled={!!selectedSurgeonDetails}
      />
      <SingleSelectInput
        placeholder="Select Speciality"
        label="Select Speciality"
        required
        register={register}
        name="speciality_id"
        errors={errors}
        data={surgeonSpecialities ?? []}
        value={detailsWatch("speciality_id") ?? ""}
        disabled={
          !!(selectedSurgeonDetails && selectedSurgeonDetails?.speciality_id)
        }
      />
    </Box>
    <Box className={style.row}>
      <Input
        placeholder="First Name"
        label="First Name"
        required
        register={register}
        name="given_name"
        errors={errors}
        disabled={!!selectedSurgeonDetails}
      />
      <Input
        placeholder="Last Name"
        label="Last Name"
        required
        register={register}
        name="surname"
        errors={errors}
        disabled={!!selectedSurgeonDetails}
      />
    </Box>
  </Box>
);

interface SurgeonAddressManagerProps {
  selectedSurgeonDetails?: Surgeon;
  onAddNewAddressClick: () => void;
  isAddNewAddress: boolean;
  isAddButton: boolean;
  onAddressSave: (deliveryAddress: DeliveryAddressPayload) => void;
  onCancelAddressForm: () => void;
  isSavingAddressDetails: boolean;
  addSurgeonSchema: AnyObjectSchema;
  register: UseFormRegister<AddSurgeonFormState>;
  errors: FieldErrors<AddSurgeonFormState>;
  setValue: UseFormSetValue<AddSurgeonFormState>;
  clearErrors: UseFormClearErrors<AddSurgeonFormState>;
  getValues: UseFormGetValues<AddSurgeonFormState>;
  onUpdateDefaultAddress: (address?: AddressDetails) => void;
  addressFieldsOptional?: boolean;
}

/**
 * Handles rendering and interaction for surgeon delivery addresses, including default selection and add/edit form.
 *
 * Args:
 *   selectedSurgeonDetails (Surgeon | undefined): Selected surgeon; controls address list rendering.
 *   onAddNewAddressClick (() => void): Shows the new address form.
 *   isAddNewAddress (boolean): Flag for address form visibility when associating existing surgeon.
 *   isAddButton (boolean): Indicates manual surgeon creation flow where address form binds to parent form.
 *   onAddressSave ((DeliveryAddressPayload) => void): Callback for saved address payload.
 *   onCancelAddressForm (() => void): Handler to close the address form.
 *   isSavingAddressDetails (boolean): Loading flag for address save/update.
 *   addSurgeonSchema (any): Validation schema used when embedding address form into parent form.
 *   register (any): Parent form register for embedded address fields.
 *   errors (any): Parent form error bag when embedded.
 *   setValue (any): Setter for parent form values.
 *   clearErrors (any): Clears parent form errors.
 *   getValues (any): Retrieves current parent form values.
 *   onUpdateDefaultAddress ((address?: AddressDetails) => void): Handler to set an address as default.
 *
 * Returns:
 *   JSX.Element: Address list and form UI.
 */
/**
 * Handles rendering and interaction for surgeon delivery addresses.
 *
 * Args:
 *   selectedSurgeonDetails (Surgeon | undefined): Selected surgeon context.
 *   onAddNewAddressClick (() => void): Shows the new address form.
 *   isAddNewAddress (boolean): Address form visibility flag.
 *   isAddButton (boolean): Manual add flow flag.
 *   onAddressSave ((DeliveryAddressPayload) => void): Address save callback.
 *   onCancelAddressForm (() => void): Cancel handler.
 *   isSavingAddressDetails (boolean): Loading flag for address saves.
 *   addSurgeonSchema (AnyObjectSchema): Schema used in embedded address form.
 *   register (UseFormRegister<AddSurgeonFormState>): Parent register.
 *   errors (FieldErrors<AddSurgeonFormState>): Parent errors bag.
 *   setValue (UseFormSetValue<AddSurgeonFormState>): Parent value setter.
 *   clearErrors (UseFormClearErrors<AddSurgeonFormState>): Parent errors clearer.
 *   getValues (UseFormGetValues<AddSurgeonFormState>): Parent values getter.
 *   onUpdateDefaultAddress ((address?: AddressDetails) => void): Default setter.
 *   addressFieldsOptional (boolean | undefined): Address optionality flag.
 *
 * Returns:
 *   JSX.Element: Address list and form UI.
 */
const SurgeonAddressManager = ({
  selectedSurgeonDetails,
  onAddNewAddressClick,
  isAddNewAddress,
  isAddButton,
  onAddressSave,
  onCancelAddressForm,
  isSavingAddressDetails,
  addSurgeonSchema,
  register,
  errors,
  setValue,
  clearErrors,
  getValues,
  onUpdateDefaultAddress,
  addressFieldsOptional,
}: SurgeonAddressManagerProps) => (
  <>
    {(isAddButton || selectedSurgeonDetails) && (
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          marginTop: "10px",
          marginBottom: "10px",
        }}
      >
        <Typography
          className={style.surgeonDetailHeading}
          sx={{ fontSize: "20px !important" }}
        >
          Report Delivery Address Details
        </Typography>
        {selectedSurgeonDetails && (
          <Button
            variant="contained"
            label={"Add New Address"}
            sx={{
              ":hover": {
                backgroundColor: colors.primary,
              },
            }}
            onClick={onAddNewAddressClick}
          />
        )}
      </Box>
    )}
    {selectedSurgeonDetails?.delivery_address &&
      selectedSurgeonDetails?.delivery_address?.map((address, index) => {
        const isDefault =
          address.id === selectedSurgeonDetails?.default_delivery_address_id;
        return (
          <Box key={address.id ?? index}>
            <AddressDetails
              address={address}
              isDefault={isDefault}
              onUpdateDefaultAddress={() => onUpdateDefaultAddress(address)}
            />
          </Box>
        );
      })}
    {(isAddNewAddress || isAddButton) && (
      <Box sx={{ marginTop: "20px" }}>
        <AddressForm
          onCancel={onCancelAddressForm}
          onSave={onAddressSave}
          isLoading={isSavingAddressDetails}
          formRegister={isAddButton ? register : undefined}
          formSchema={isAddButton ? addSurgeonSchema : undefined}
          formErrors={isAddButton ? errors : undefined}
          setFormValue={isAddButton ? setValue : undefined}
          clearFormErrors={isAddButton ? clearErrors : undefined}
          getFormValues={isAddButton ? getValues : undefined}
          addressFieldsOptional={addressFieldsOptional}
        />
      </Box>
    )}
  </>
);

export default AddSurgeon;
